import { useEffect, useState } from 'react';
import { redirectToAuth } from 'supertokens-auth-react';
import SuperTokens from 'supertokens-auth-react/ui';
import { PasswordlessPreBuiltUI } from 'supertokens-auth-react/recipe/passwordless/prebuiltui';
import { useTheme } from '@/components/providers';
import { Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function AuthPage() {
  const [mounted, setMounted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (SuperTokens.canHandleRoute([PasswordlessPreBuiltUI]) === false) {
      redirectToAuth({ redirectBack: false });
    } else {
      setLoaded(true);
    }
  }, []);

  if (!mounted || !loaded) return null;

  const logoSrc =
    theme === 'dark'
      ? '/images/logos/cogknit-logo-white.png'
      : '/images/logos/cogknit-logo-black.png';

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <div className="relative min-h-screen w-full flex flex-col items-center justify-center bg-background text-foreground transition-all duration-500">

      {/* Soft Background Accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,hsl(var(--primary)/0.05)_0%,transparent_50%)]" />
      </div>

      {/* Floating Theme Toggle */}
      <div className="absolute top-6 right-6 z-50">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="rounded-xl bg-background/50 backdrop-blur-md border border-border/50 hover:bg-background/80"
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
      </div>

      {/* Main Content Container */}
      <main className="relative z-10 w-full max-w-[440px] px-6 py-12 flex flex-col items-center animate-in fade-in duration-700">

        {/* Simplified Header */}
        <div className="mb-10 flex flex-col items-center justify-center">
          <img
            src={logoSrc}
            alt="CogKnit"
            className="h-14 w-64 object-contain"
          />
        </div>

        {/* Auth Card */}
        <div className="w-full bg-background/40 backdrop-blur-2xl border border-border/50 rounded-3xl shadow-xl overflow-hidden transition-all duration-500">
          <div className="p-8 sm:p-10">
            <div
              className="
                [&_.formContainer]:space-y-6
                [&_.headerTitle]:text-2xl [&_.headerTitle]:font-bold [&_.headerTitle]:text-foreground [&_.headerTitle]:text-center [&_.headerTitle]:mb-2
                [&_.headerSubtitle]:text-sm [&_.headerSubtitle]:text-muted-foreground [&_.headerSubtitle]:text-center [&_.headerSubtitle]:mb-8
                
                /* Input Styling */
                [&_input]:h-12
                [&_input]:rounded-xl
                [&_input]:border-border/60
                [&_input]:bg-background/50
                [&_input]:px-4
                [&_input]:text-base
                [&_input:focus]:border-primary/50 [&_input:focus]:ring-4 [&_input:focus]:ring-primary/10
                
                /* Button Styling */
                [&_button]:h-12
                [&_button]:rounded-xl
                [&_button]:bg-primary
                [&_button]:text-primary-foreground
                [&_button]:font-bold
                [&_button]:text-sm
                [&_button:hover]:bg-primary/90
                
                /* Layout & Link Fixes */
                [&_.link]:text-primary [&_.link]:font-semibold [&_.link]:no-underline [&_.link:hover]:underline
                
                /* OTP UI Adjustments for Responsiveness */
                [&_[data-supertokens~='row']]:flex [&_[data-supertokens~='row']]:flex-col [&_[data-supertokens~='row']]:gap-1
                [&_[data-supertokens~='container']]:max-w-full
                [&_[data-supertokens~='resendCode']]:text-xs [&_[data-supertokens~='resendCode']]:text-primary [&_[data-supertokens~='resendCode']]:font-medium [&_[data-supertokens~='resendCode']]:hover:underline [&_[data-supertokens~='resendCode']]:mt-1
                [&_[data-supertokens~='inputContainer']]:w-full
                [&_[data-supertokens~='secondaryText']]:text-center [&_[data-supertokens~='secondaryText']]:w-full
              "
            >
              {SuperTokens.getRoutingComponent([PasswordlessPreBuiltUI])}
            </div>

            {/* Simple Footer Links */}

          </div>
        </div>

        {/* Global Support Info */}
        <footer className="mt-10 flex flex-col items-center gap-4">
          <a
            href="mailto:support@cogknit.io"
            className="text-xs font-semibold text-muted-foreground hover:text-primary transition-colors"
          >
            Need help? Contact support
          </a>
          <span className="text-[10px] text-muted-foreground/30 font-bold tracking-widest uppercase">
            © {new Date().getFullYear()} CogKnit
          </span>
        </footer>
      </main>

    </div>
  );
}
